import {
  acceptTimeWords,
  hoursWords,
  WAITLIST_ACCEPT_MINUTES_CHOICES,
  WAITLIST_ACCEPT_MINUTES_DEFAULT,
  WAITLIST_OFFER_CUTOFF_HOURS,
  WAITLIST_LATE_HOURS_CHOICES,
  WAITLIST_LATE_HOURS_DEFAULT,
  waitlistOffersOn,
} from "@teamna/shared";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { FormSection, InlineError, SelectField, showToast } from "../components/index.ts";

/** The choices, with the saved value when it is not one of them (set another way). */
function withCurrent(choices: readonly number[], current: number): number[] {
  return choices.includes(current) ? [...choices] : [...choices, current].sort((a, b) => a - b);
}

/**
 * "Waiting list" (Pilates studio shape B2), on the Packages and rules page. A place that opens early
 * is booked for the first person in line. A place that opens late goes to them as an offer, with a
 * time to accept it. The owner sets when "late" starts and how long the time to accept is. A change
 * applies to the next offers; it sends nothing. Only where clients book for themselves
 * (`waitlistOffersOn`): a family business books every free place at once, as before.
 */
export function WaitlistRuleCard({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  const settings = business.data?.settings;
  const late = settings?.waitlistLateHours ?? WAITLIST_LATE_HOURS_DEFAULT;
  const accept = settings?.waitlistAcceptMinutes ?? WAITLIST_ACCEPT_MINUTES_DEFAULT;
  const member = label("member");
  const disabled = update.isPending || !business.data;
  const { shape, ready } = useShape(businessId);
  if (!ready || !waitlistOffersOn(shape.bookingFor)) return null;

  return (
    <FormSection
      title="Waiting list"
      description={`When a full session gets a free place, the first ${member} in line gets it. Early, Teamna books it for them and uses 1 session. Late, they get a push (and a WhatsApp message where WhatsApp is on), and a time to accept. If they do not accept, the next person in line gets the offer. The list closes ${hoursWords(WAITLIST_OFFER_CUTOFF_HOURS)} before the start.`}
    >
      <SelectField
        label="A place opens late when it is less than"
        hint="Before this time, Teamna books the place at once."
        value={String(late)}
        disabled={disabled}
        onChange={(event) => {
          const next = Number(event.target.value);
          update.mutate({ settings: { waitlistLateHours: next } }, { onSuccess: () => showToast(`Late places start ${hoursWords(next)} before a session.`) });
        }}
      >
        {withCurrent(WAITLIST_LATE_HOURS_CHOICES, late).map((hours) => (
          <option key={hours} value={String(hours)}>
            {hoursWords(hours)} before the start
          </option>
        ))}
      </SelectField>
      <SelectField
        label="Time to accept an offer"
        hint={`An offer always ends ${hoursWords(WAITLIST_OFFER_CUTOFF_HOURS)} before the start, also when this time is longer.`}
        value={String(accept)}
        disabled={disabled}
        onChange={(event) => {
          const next = Number(event.target.value);
          update.mutate({ settings: { waitlistAcceptMinutes: next } }, { onSuccess: () => showToast(`New offers give ${acceptTimeWords(next)} to accept.`) });
        }}
      >
        {withCurrent(WAITLIST_ACCEPT_MINUTES_CHOICES, accept).map((minutes) => (
          <option key={minutes} value={String(minutes)}>
            {acceptTimeWords(minutes)}
          </option>
        ))}
      </SelectField>
      {update.error ? <InlineError error={update.error} /> : null}
    </FormSection>
  );
}
